import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtMoney = (n) => '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pad = (s, n, right = false) => {
  s = String(s ?? '');
  if (s.length >= n) return s;
  const filler = ' '.repeat(n - s.length);
  return right ? s + filler : filler + s;
};

export default function CogsDiagnostic() {
  // Section 1: per-month diagnostic
  const [data, setData] = useState(null);
  const [dataErr, setDataErr] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Section 2: COGS coverage by line-item product name (grouped view)
  const [coverage, setCoverage] = useState(null);
  const [coverageErr, setCoverageErr] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [products, setProducts] = useState(null);
  const [productsErr, setProductsErr] = useState(null);
  const [productsLoading, setProductsLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Pending per group key. Key: `${shop_id}::${line_item_name}`.
  // Value: { productId?, productLabel?, ignore? }
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const loadDiagnostic = async () => {
    setDataLoading(true); setDataErr(null);
    try {
      const r = await api.get('/api/admin/cogs-diagnostic');
      setData(r.data);
    } catch (e) {
      setDataErr(e?.response?.data?.error || e.message);
    } finally { setDataLoading(false); }
  };

  const loadCoverage = async () => {
    setCoverageLoading(true); setCoverageErr(null);
    try {
      const r = await api.get('/api/admin/cogs-coverage-by-product');
      setCoverage(r.data.rows || []);
    } catch (e) {
      setCoverageErr(e?.response?.data?.error || e.message);
    } finally { setCoverageLoading(false); }
  };

  const loadProducts = async () => {
    setProductsLoading(true); setProductsErr(null);
    try {
      const r = await api.get('/api/admin/products-lite');
      setProducts(r.data.rows || []);
    } catch (e) {
      setProductsErr(e?.response?.data?.error || e.message);
    } finally { setProductsLoading(false); }
  };

  useEffect(() => {
    loadDiagnostic();
    loadCoverage();
    loadProducts();
  }, []);

  // --- Section 1 text rendering ---
  let diagnosticText = '';
  if (data) {
    const headerCells = [
      ['Month', 7, true],
      ['#Brks', 6, false],
      ['wActual', 8, false],
      ['wExpOnly', 9, false],
      ['NoCOGS', 7, false],
      ['SumActual', 12, false],
      ['SumExpFB', 12, false],
      ['BreakLvlTot', 13, false],
      ['BP_COGS', 12, false],
      ['BP#', 5, false],
      ['Order_COGS', 13, false],
      ['LI#', 5, false],
      ['Imported', 12, false],
      ['ImpDay#', 8, false],
      ['Freebie', 12, false],
      ['Frb#', 5, false],
      ['FinancialsTotal', 16, false],
      ['InsightsTotal', 14, false],
      ['Diff(F-I)', 12, false],
    ];
    const header = headerCells.map(([t, n, r]) => pad(t, n, r)).join(' | ');
    const divider = '-'.repeat(header.length);
    const rows = data.months.map(m => [
      [m.month, 7, true],
      [m.break_count, 6, false],
      [m.with_actual, 8, false],
      [m.with_expected_only, 9, false],
      [m.no_cogs, 7, false],
      [fmt(m.sum_actual), 12, false],
      [fmt(m.sum_expected_fallback), 12, false],
      [fmt(m.break_level_total), 13, false],
      [fmt(m.bp_cogs), 12, false],
      [m.bp_row_count, 5, false],
      [fmt(m.order_cogs), 13, false],
      [m.line_item_count, 5, false],
      [fmt(m.imported_cogs), 12, false],
      [m.imported_day_count, 8, false],
      [fmt(m.freebie_cogs), 12, false],
      [m.freebie_row_count, 5, false],
      [fmt(m.financials_total), 16, false],
      [fmt(m.insights_total), 14, false],
      [fmt(m.diff_financials_minus_insights), 12, false],
    ].map(([t, n, r]) => pad(t, n, r)).join(' | '));

    const covHeader = [
      ['Month', 8, true],
      ['LI Total', 9, false],
      ['Matched', 9, false],
      ['MatchedCogs', 12, false],
      ['Unmatched', 11, false],
      ['MatchedNoCogs', 14, false],
      ['Order_COGS', 13, false],
      ['MatchRate%', 11, false],
      ['CogsCovRate%', 13, false],
    ].map(([t, n, r]) => pad(t, n, r)).join(' | ');
    const covDivider = '-'.repeat(covHeader.length);
    const covRows = (data.orderCoverage || []).map(r => {
      const matchedNoCogs = r.line_items_matched - r.line_items_with_cogs;
      const matchRate = r.line_items_total > 0 ? (r.line_items_matched / r.line_items_total * 100).toFixed(1) : '—';
      const cogsCov = r.line_items_total > 0 ? (r.line_items_with_cogs / r.line_items_total * 100).toFixed(1) : '—';
      return [
        [r.month, 8, true],
        [r.line_items_total, 9, false],
        [r.line_items_matched, 9, false],
        [r.line_items_with_cogs, 12, false],
        [r.line_items_unmatched, 11, false],
        [matchedNoCogs, 14, false],
        [fmt(r.order_cogs_recomputed), 13, false],
        [matchRate, 11, false],
        [cogsCov, 13, false],
      ].map(([t, n, rgt]) => pad(t, n, rgt)).join(' | ');
    });

    const groupBy = (arr, key) => arr.reduce((acc, x) => { (acc[x[key]] ||= []).push(x); return acc; }, {});
    const contribByMonth = groupBy(data.topContributors || [], 'month');
    const unmatchedByMonth = groupBy(data.topUnmatched || [], 'month');
    const noCogsByMonth = groupBy(data.matchedNoCogs || [], 'month');

    const orderedMonths = [...new Set([
      ...(data.orderCoverage || []).map(r => r.month),
      ...(data.months || []).map(r => r.month),
    ])].sort();

    const detailLines = [];
    for (const m of orderedMonths) {
      const contrib = contribByMonth[m] || [];
      const unm = unmatchedByMonth[m] || [];
      const nc = noCogsByMonth[m] || [];
      detailLines.push('');
      detailLines.push(`========== ${m} ==========`);
      if (contrib.length) {
        detailLines.push('  Top 5 Order_COGS contributors:');
        for (const c of contrib) detailLines.push(`    $${fmt(c.contrib)}  qty=${c.qty_sold}  ${c.product_name}`);
      }
      if (nc.length) {
        detailLines.push('  Top 5 MATCHED products with NULL cogs:');
        for (const x of nc) detailLines.push(`    GMV=$${fmt(x.gmv_no_cogs)}  qty=${x.qty_no_cogs}  ${x.product_name}`);
      }
      if (unm.length) {
        detailLines.push('  Top 5 UNMATCHED line items:');
        for (const x of unm) detailLines.push(`    GMV=$${fmt(x.gmv_unmatched)}  qty=${x.qty_unmatched}  sku=${x.sku_id}  name=${x.line_item_name || '—'}`);
      }
    }

    diagnosticText = [
      'COGS DIAGNOSTIC — Nucleus',
      `Generated: ${new Date().toISOString()}`,
      '',
      '=== SECTION 1: Per-month COGS by source ===',
      'BreakLvlTot = SUM(COALESCE(actual_cogs, expected_cogs, 0))  — what Insights uses',
      'FinancialsTotal = BP_COGS + Order_COGS + Imported            — what the Dashboard/Financials uses (pre-fix)',
      'Diff(F-I) = FinancialsTotal − InsightsTotal                  — discrepancy between the two views',
      '',
      header, divider, ...rows,
      '',
      '=== SECTION 2: Order_COGS coverage ===',
      covHeader, covDivider, ...covRows,
      '',
      '=== SECTION 3: Per-month top contributors / gaps ===',
      ...detailLines,
    ].join('\n');
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(diagnosticText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  // --- Section 2 helpers ---
  const key = (r) => `${r.shop_id}::${r.line_item_name}`;
  const updatePending = (r, patch) => setPending(prev => ({ ...prev, [key(r)]: { ...(prev[key(r)] || {}), ...patch } }));

  const filtered = useMemo(() => {
    const list = coverage || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(r => (r.line_item_name || '').toLowerCase().includes(q));
  }, [coverage, search]);

  // Build the product picker as a single shared <datalist>. Without this the
  // page rendered a <select> per row, each with every product as <option> —
  // ~50k DOM nodes was enough to lock the tab.
  const productOptions = useMemo(() => {
    return (products || []).map(p => ({
      id: p.id,
      label: `${p.product_name || p.product_id} ${p.cogs != null ? `· $${Number(p.cogs).toFixed(2)} COGS` : '· no COGS'}`,
    }));
  }, [products]);
  const labelToProductId = useMemo(() => {
    const m = new Map();
    for (const o of productOptions) m.set(o.label, o.id);
    return m;
  }, [productOptions]);

  // For each Nucleus product, lower-cased name → option object. Used to
  // auto-suggest a product when the line-item name matches exactly (or one
  // contains the other) and to back the "Auto-map name matches" button.
  const productsByName = useMemo(() => {
    const m = new Map();
    for (const p of (products || [])) {
      const name = (p.product_name || '').trim().toLowerCase();
      if (!name) continue;
      const existing = m.get(name);
      // Prefer products with cogs set if there are duplicates.
      if (!existing || (existing.cogs == null && p.cogs != null)) m.set(name, p);
    }
    return m;
  }, [products]);

  const findSuggestion = (lineItemName) => {
    if (!lineItemName) return null;
    const target = lineItemName.trim().toLowerCase();
    const exact = productsByName.get(target);
    if (exact) return exact;
    // Fall back to substring match either direction.
    for (const [name, p] of productsByName.entries()) {
      if (target.includes(name) || name.includes(target)) return p;
    }
    return null;
  };

  const labelForProduct = (p) =>
    `${p.product_name || p.product_id} ${p.cogs != null ? `· $${Number(p.cogs).toFixed(2)} COGS` : '· no COGS'}`;

  const autoMapAll = () => {
    const next = { ...pending };
    let count = 0;
    for (const r of (coverage || [])) {
      const k = key(r);
      if (next[k]?.productId || next[k]?.ignore) continue;
      const suggested = findSuggestion(r.line_item_name);
      if (suggested) {
        next[k] = { productId: suggested.id, productLabel: labelForProduct(suggested), ignore: false };
        count++;
      }
    }
    setPending(next);
    setSaveMsg(`Auto-filled ${count} row(s). Click Save to apply.`);
    setTimeout(() => setSaveMsg(''), 4000);
  };

  // For each pending group: fetch its unmatched SKU list, then either create
  // aliases for all of them or add all of them to the ignore list.
  const save = async () => {
    const pendingRows = (coverage || []).filter(r => {
      const p = pending[key(r)];
      return p && (p.ignore || p.productId);
    });
    if (!pendingRows.length) {
      setSaveMsg('Nothing to save.');
      setTimeout(() => setSaveMsg(''), 2000);
      return;
    }
    setSaving(true);
    try {
      const aliases = [];
      const ignores = [];
      // Resolve each group's SKU list (server-side, so unmapped SKUs only).
      for (const r of pendingRows) {
        const p = pending[key(r)];
        const { data } = await api.get('/api/admin/cogs-coverage-by-product/skus', {
          params: { shopId: r.shop_id, name: r.line_item_name },
        });
        for (const s of (data.rows || [])) {
          if (p.ignore) ignores.push({ shop_id: r.shop_id, sku_id: s.sku_id, line_item_name: r.line_item_name });
          else if (p.productId) aliases.push({ shop_id: r.shop_id, alias_sku_id: s.sku_id, product_internal_id: p.productId });
        }
      }
      if (!aliases.length && !ignores.length) {
        setSaveMsg('No unmapped SKUs to act on (they may all be covered already).');
        setTimeout(() => setSaveMsg(''), 4000);
        return;
      }
      const { data: result } = await api.post('/api/admin/sku-aliases/bulk', { aliases, ignores });
      setSaveMsg(`Saved ${result.aliasCount} alias(es), ${result.ignoreCount} ignore(s) across ${pendingRows.length} product(s).`);
      setPending({});
      await Promise.all([loadCoverage(), loadDiagnostic()]);
    } catch (e) {
      setSaveMsg('Error: ' + (e?.response?.data?.error || e.message));
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(''), 6000);
    }
  };

  const pendingCount = Object.values(pending).filter(p => p?.ignore || p?.productId).length;

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginTop: 0 }}>COGS Diagnostic</h2>

      {/* --- Per-month diagnostic --- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Per-month dump</h3>
        <button className="btn btn-sm btn-primary" onClick={copy} disabled={!data}>{copied ? 'Copied!' : 'Copy as text'}</button>
        <button className="btn btn-sm btn-secondary" onClick={loadDiagnostic} disabled={dataLoading}>{dataLoading ? 'Loading…' : 'Refresh'}</button>
        {dataErr && <span style={{ color: 'var(--danger)' }}>Error: {dataErr}</span>}
      </div>
      {dataLoading && !data && <div style={{ color: 'var(--text-secondary)' }}>Loading diagnostic…</div>}
      {data && (
        <pre style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 12, whiteSpace: 'pre',
          background: '#0b0f16', color: '#d6deeb',
          padding: 16, borderRadius: 6, overflow: 'auto', maxHeight: '60vh', marginTop: 8,
        }}>{diagnosticText}</pre>
      )}

      {/* --- COGS coverage by product --- */}
      <h3 style={{ marginTop: 32 }}>Products with COGS gaps</h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 0 }}>
        Each row is a product (by line-item name) where some orders sold without contributing to COGS. <strong>Gap Qty</strong> is how many units sold without COGS; <strong>Gap GMV</strong> is the dollar value of those sales. Sorted by Gap GMV — fix the top rows first.
        For each row, pick a Nucleus product to alias every un-matched SKU under that name (closes the gap, including historically), or Ignore the name (treat as no-COGS, like break spots).
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search product name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border, #ccc)', minWidth: 240 }}
        />
        <button className="btn btn-sm btn-secondary" onClick={loadCoverage} disabled={coverageLoading || saving}>
          {coverageLoading ? 'Loading…' : 'Refresh'}
        </button>
        <button
          className="btn btn-sm btn-secondary"
          onClick={autoMapAll}
          disabled={saving || productsLoading || coverageLoading}
          title="Pre-fill every row where the line-item name matches a Nucleus product"
        >
          Auto-fill name matches
        </button>
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          {(coverage || []).length} products with gaps · {(products || []).length} Nucleus products{productsLoading ? ' (loading…)' : ''}
        </span>
        <div style={{ flex: 1 }} />
        {pendingCount > 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{pendingCount} pending</span>}
        <button className="btn btn-sm btn-primary" onClick={save} disabled={saving || pendingCount === 0}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saveMsg && <span style={{ fontSize: 13, color: saveMsg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)' }}>{saveMsg}</span>}
      </div>

      {coverageErr && <div style={{ color: 'var(--danger)', marginBottom: 8 }}>Coverage error: {coverageErr}</div>}
      {productsErr && <div style={{ color: 'var(--danger)', marginBottom: 8 }}>Products error: {productsErr}</div>}

      <datalist id="cogs-products-list">
        {productOptions.map(opt => (
          <option key={opt.id} value={opt.label} />
        ))}
      </datalist>

      {coverageLoading && !coverage ? (
        <div style={{ color: 'var(--text-secondary)' }}>Loading product coverage…</div>
      ) : (
        <div className="scroll-x sticky-col" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border, #ccc)', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px' }}>Product (line-item name)</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>Total Qty</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>Qty w/ COGS</th>
                <th style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--danger, #c00)' }}>Gap Qty</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>Total GMV</th>
                <th style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--danger, #c00)' }}>Gap GMV</th>
                <th style={{ padding: '8px 6px', textAlign: 'center' }}>#SKUs</th>
                <th style={{ padding: '8px 6px', minWidth: 280 }}>Map all unmapped SKUs to product</th>
                <th style={{ padding: '8px 6px', textAlign: 'center' }}>Ignore</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const p = pending[key(r)] || {};
                const coveragePct = r.total_qty > 0 ? (r.cogs_qty / r.total_qty * 100) : 0;
                const suggestion = !p.productId && !p.ignore ? findSuggestion(r.line_item_name) : null;
                return (
                  <tr key={key(r)} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                    <td style={{ padding: '6px', maxWidth: 420 }}>
                      <div>{r.line_item_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        coverage {coveragePct.toFixed(0)}% · {r.unmatched_line_count} unmatched line items{r.matched_nocogs_line_count > 0 ? ` · ${r.matched_nocogs_line_count} matched-but-no-COGS` : ''}
                      </div>
                      {suggestion && (
                        <div style={{ fontSize: 11, marginTop: 2 }}>
                          <button
                            onClick={() => updatePending(r, { productId: suggestion.id, productLabel: labelForProduct(suggestion), ignore: false })}
                            style={{ background: 'transparent', border: 'none', color: 'var(--primary, #2563eb)', cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}
                          >
                            ✨ Use {suggestion.product_name}{suggestion.cogs != null ? ` ($${Number(suggestion.cogs).toFixed(2)} COGS)` : ' (no COGS set)'}
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{r.total_qty}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{r.cogs_qty}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: 'var(--danger, #c00)' }}>{r.gap_qty}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{fmtMoney(r.total_gmv)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: 'var(--danger, #c00)' }}>{fmtMoney(r.gap_gmv)}</td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>{r.sku_count}</td>
                    <td style={{ padding: '6px' }}>
                      <input
                        type="text"
                        list="cogs-products-list"
                        placeholder={productsLoading ? 'loading products…' : 'type to search Nucleus products…'}
                        value={p.productLabel || ''}
                        onChange={e => {
                          const v = e.target.value;
                          const matchId = labelToProductId.get(v);
                          updatePending(r, {
                            productLabel: v,
                            productId: matchId || null,
                            ignore: false,
                          });
                        }}
                        disabled={!!p.ignore || productsLoading}
                        style={{ width: '100%', padding: 4, border: p.productLabel && !p.productId ? '1px solid var(--danger, #c00)' : '1px solid var(--border, #ccc)', borderRadius: 4 }}
                      />
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!p.ignore}
                        onChange={e => updatePending(r, { ignore: e.target.checked, productId: null, productLabel: '' })}
                      />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 16, textAlign: 'center', color: 'var(--text-secondary)' }}>
                  {(coverage || []).length === 0 ? 'No COGS gaps — every selling product is covered.' : 'No products match the search.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
